import { useHandler } from "@redotech/react-util/hook";
import { memo, useEffect, useState } from "react";
import { RedoBadgeColor } from "../arbiter-components/badge/redo-badge";
import { RedoListItem } from "../arbiter-components/list/redo-list";
import {
  RedoDropdownInputSize,
  RedoSingleSelectDropdownInput,
} from "../arbiter-components/select-dropdown/redo-single-select-dropdown-input";

export interface DropdownLocation {
  id: string;
  name: string;
  redoOnly?: boolean | null;
}

const redoOnlyBadge = {
  text: "Non-marketplace",
  color: "orange" as RedoBadgeColor,
};

function locationToListItem(
  location: DropdownLocation,
): RedoListItem<DropdownLocation> {
  return {
    id: location.id,
    type: "text",
    text: location.name,
    value: location,
    ...(location.redoOnly ? { infoBadge: redoOnlyBadge } : {}),
  };
}

export const LocationDropdown = memo(function LocationDropdown({
  selectedLocationId,
  onSelectedLocationChange,
  loading,
  locations,
  mixed,
  disabled,
  hideLabel,
  size,
}: {
  selectedLocationId: string | null;
  onSelectedLocationChange: (locationId: string) => void;
  locations: readonly DropdownLocation[];
  loading: boolean;
  mixed: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  size?: RedoDropdownInputSize;
}) {
  const [selectedLocation, setSelectedLocation] =
    useState<DropdownLocation | null>(null);

  useEffect(() => {
    if (!loading) {
      setSelectedLocation(
        locations.find((location) => location.id === selectedLocationId) ??
          null,
      );
    }
  }, [locations, selectedLocationId, loading]);

  const onChange = useHandler((item: RedoListItem<DropdownLocation>) => {
    if (item.value.id !== selectedLocation?.id) {
      setSelectedLocation(item.value);
      onSelectedLocationChange(item.value.id);
    }
  });

  return (
    <RedoSingleSelectDropdownInput<DropdownLocation>
      disabled={loading || disabled}
      itemsLoading={loading}
      label={hideLabel ? null : "Location"}
      options={locations.map(locationToListItem)}
      optionSelected={(item) => onChange(item.item)}
      placeholder={mixed ? "{mixed}" : "Select location"}
      selectedItem={
        selectedLocation ? locationToListItem(selectedLocation) : undefined
      }
      size={size ?? "xs"}
    />
  );
});

export const WarehouseBuilderLocationDropdown = memo(
  function WarehouseBuilderLocationDropdown({
    selectedLocationId,
    onSelectedLocationChange,
    loading,
    locations,
    disabled,
  }: {
    selectedLocationId: string | null;
    onSelectedLocationChange: (locationId: string) => void;
    locations: readonly DropdownLocation[];
    loading: boolean;
    disabled?: boolean;
  }) {
    const [selectedLocation, setSelectedLocation] =
      useState<DropdownLocation | null>(null);

    useEffect(() => {
      if (!loading) {
        setSelectedLocation(
          locations.find((location) => location.id === selectedLocationId) ??
            null,
        );
      }
    }, [locations, selectedLocationId, loading]);

    const onChange = useHandler((item: RedoListItem<DropdownLocation>) => {
      if (item.value.id !== selectedLocation?.id) {
        setSelectedLocation(item.value);
        onSelectedLocationChange(item.value.id);
      }
    });

    return (
      <RedoSingleSelectDropdownInput<DropdownLocation>
        disabled={loading || disabled}
        itemsLoading={loading}
        options={locations.map((location) => ({
          id: location.id,
          type: "text",
          text: location.name,
          value: location,
        }))}
        optionSelected={(item) => onChange(item.item)}
        placeholder="Select location"
        selectedItem={
          selectedLocation
            ? {
                id: selectedLocation.id,
                type: "text",
                text: selectedLocation.name,
                value: selectedLocation,
              }
            : undefined
        }
        size="xs"
      />
    );
  },
);
